import React from 'react';
import { Link } from 'react-router-dom';
import { Logo } from '../components/UI';

function Section({ title, children }) {
  return (
    <div className="mb-8">
      <h2 className="font-serif font-semibold text-green-900 text-lg mb-3 pb-2 border-b border-amber-100">{title}</h2>
      <div className="text-gray-600 text-sm leading-relaxed space-y-3">{children}</div>
    </div>
  );
}

export default function PrivacyPolicyPage() {
  const updated = 'March 8, 2026';

  return (
    <div className="min-h-screen flex flex-col">
      {/* Nav */}
      <nav className="bg-white border-b border-amber-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-5 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Logo size="sm" />
            <span className="font-serif font-bold text-green-900 text-lg">GolfBoard Scores</span>
          </Link>
          <Link to="/login" className="btn-primary text-sm">Sign In</Link>
        </div>
      </nav>

      <main className="flex-1 page-container py-10">
        <div className="max-w-2xl mx-auto">
          <h1 className="font-serif font-bold text-3xl text-green-900 mb-1">Privacy Policy</h1>
          <p className="text-sm text-gray-400 mb-10">Last updated: {updated}</p>

          <Section title="1. Introduction">
            <p>GolfBoard Scores ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard information when you use our platform. By using GolfBoard Scores, you agree to the practices described in this policy.</p>
          </Section>

          <Section title="2. Information We Collect">
            <p>We collect the following types of information:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Account Information:</strong> Email address and username. Accounts may be created via email/password or Google Sign-In. Passwords are stored securely via Supabase Auth and are never stored if you sign in with Google.</li>
              <li><strong>Profile Information:</strong> Optional handicap index and profile photo you choose to upload.</li>
              <li><strong>Score Data:</strong> Golf scores, tee selections, over/under par, and the date each score was posted.</li>
              <li><strong>Usage Data:</strong> General usage patterns to help us improve the platform. We do not use invasive tracking or sell behavioral data.</li>
            </ul>
          </Section>

          <Section title="3. How We Use Your Information">
            <p>We use the information we collect to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Provide, operate, and maintain the GolfBoard Scores platform.</li>
              <li>Display your scores and profile on public leaderboards.</li>
              <li>Send transactional emails (e.g., password reset) when required.</li>
              <li>Detect and prevent fraudulent or abusive activity.</li>
              <li>Respond to support requests submitted through the platform.</li>
            </ul>
          </Section>

          <Section title="4. Information That Is Public">
            <p>The following information is visible to all users of GolfBoard Scores, including unauthenticated visitors browsing public leaderboards:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Your username</li>
              <li>Your posted scores (course, tee, total score, over/under par, date)</li>
              <li>Your profile photo (if uploaded)</li>
              <li>Your handicap index (if provided)</li>
            </ul>
            <p>Please do not post information on GolfBoard Scores that you wish to keep private.</p>
          </Section>

          <Section title="5. Data Storage & Security">
            <p>GolfBoard Scores uses Supabase for authentication and database storage. All data is stored in secure, encrypted databases. Passwords are never stored in plain text.</p>
            <p>Profile photos are stored in Supabase Storage (cloud object storage) and served via public URLs. You can replace or remove your avatar at any time from your profile settings.</p>
            <p>While we take reasonable steps to protect your data, no internet transmission is 100% secure. Use GolfBoard Scores at your own risk.</p>
          </Section>

          <Section title="6. Cookies & Local Storage">
            <p>GolfBoard Scores uses your browser's local storage to store your authentication token so you remain logged in between sessions. We do not use advertising cookies or third-party tracking cookies at this time.</p>
            <p>If Google AdSense is enabled in the future to support GolfBoard Scores' free operation, advertising cookies may be placed by Google. We will update this policy before enabling ad services.</p>
          </Section>

          <Section title="7. Third-Party Services">
            <p>GolfBoard Scores uses the following third-party services to operate:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Supabase</strong> — database, authentication, and file storage.</li>
              <li><strong>Google OAuth</strong> — if you choose to sign in with Google, Google will share your name and email address with us to create your account. Google's privacy policy applies to that interaction.</li>
              <li><strong>Golf Course API</strong> — to retrieve course, tee, and yardage data.</li>
            </ul>
            <p>These services have their own privacy policies. We do not sell your personal information to any third party.</p>
          </Section>

          <Section title="8. Data Retention & Deletion">
            <p>We retain your account and score data for as long as your account remains active. If you wish to delete your account and associated data, please contact us using the link in the footer. We will process deletion requests within a reasonable timeframe.</p>
          </Section>

          <Section title="9. Children's Privacy">
            <p>GolfBoard Scores is not directed at children under the age of 13. We do not knowingly collect personal information from children. If you believe a child has provided us with personal information, please contact us so we can delete it.</p>
          </Section>

          <Section title="10. Changes to This Policy">
            <p>We may update this Privacy Policy from time to time. When we do, we will update the "Last updated" date at the top of this page. Continued use of GolfBoard Scores after any changes constitutes acceptance of the updated policy.</p>
          </Section>

          <Section title="11. Contact">
            <p>If you have questions or concerns about this Privacy Policy or how your data is handled, please reach out via our <Link to="/contact" className="text-amber-700 hover:underline font-medium">Contact page</Link>.</p>
          </Section>
        </div>
      </main>

      <footer className="bg-green-900 text-green-300 py-8 px-5 mt-10">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
          <div className="flex items-center gap-2">
            <Logo size="sm" />
            <span className="font-serif font-semibold text-amber-200">GolfBoard</span>
          </div>
          <div className="flex gap-5 text-xs">
            <Link to="/" className="hover:text-amber-200">Home</Link>
            <Link to="/terms" className="hover:text-amber-200">Terms</Link>
            <Link to="/contact" className="hover:text-amber-200">Contact</Link>
          </div>
          <p className="text-green-500 text-xs">© {new Date().getFullYear()} GolfBoard Scores.</p>
        </div>
      </footer>
    </div>
  );
}